import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { TiminatorTableColumn } from '../types';
import { useFixedHorizontalScrollbar } from './useFixedHorizontalScrollbar';
import { useTableSorting } from './useTableSorting';

const LOAD_BATCH_SIZE = 50;
const DEFAULT_COLUMN_WIDTH = '1fr';
const DRAG_SCROLL_THRESHOLD = 6;
const WHEEL_SCROLL_THRESHOLD = 0.5;

const normalizeGridWidth = (width?: string | number) => {
    if (typeof width === 'number') {
        return `${width}px`;
    }

    return width ?? DEFAULT_COLUMN_WIDTH;
};

interface UseTableControllerProps<Entity> {
    columns: TiminatorTableColumn<Entity>[];
    data: Entity[] | null;
    onRowClick?: (item: Entity) => void;
    hasMore?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    desktopMinWidth?: string | number;
}

export function useTableController<Entity>({
    columns,
    data,
    onRowClick,
    hasMore,
    onLoadMore,
    isLoadingMore,
    desktopMinWidth,
}: UseTableControllerProps<Entity>) {
    const isDesktop = useMediaQuery({ minWidth: 1001 });
    const sentinelRef = React.useRef<HTMLDivElement | null>(null);
    const { sortedData, sortConfig, handleSortToggle } = useTableSorting(data, columns);
    const sourceData = React.useMemo(() => (sortedData ?? data ?? []), [sortedData, data]);
    const {
        viewportRef,
        contentRef,
        scrollbarRef,
        contentWidth,
        scrollLeft,
        showScrollbar,
        scrollbarStyle,
        measure,
        handleScrollbarScroll,
        setScrollLeft,
    } = useFixedHorizontalScrollbar(isDesktop);
    const dragStateRef = React.useRef({
        pointerId: -1,
        startX: 0,
        lastX: 0,
        isDragging: false,
        suppressClick: false,
    });
    const suppressClickTimeoutRef = React.useRef<number | null>(null);

    const [visibleCount, setVisibleCount] = React.useState(() => {
        if (!data || data.length === 0) {
            return 0;
        }
        return Math.min(LOAD_BATCH_SIZE, data.length);
    });

    React.useEffect(() => {
        if (sourceData.length === 0) {
            setVisibleCount(0);
            return;
        }

        setVisibleCount(prev => {
            if (prev === 0) {
                return Math.min(LOAD_BATCH_SIZE, sourceData.length);
            }

            if (prev > sourceData.length) {
                return sourceData.length;
            }

            return prev;
        });
    }, [sourceData]);

    React.useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) {
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (!entry || !entry.isIntersecting) {
                return;
            }

            if (sourceData.length > 0 && visibleCount < sourceData.length) {
                setVisibleCount(prev => {
                    if (sourceData.length === 0) {
                        return 0;
                    }
                    if (prev >= sourceData.length) {
                        return sourceData.length;
                    }
                    return Math.min(prev + LOAD_BATCH_SIZE, sourceData.length);
                });
                return;
            }

            if (hasMore && onLoadMore && !isLoadingMore) {
                onLoadMore();
            }
        }, {
            root: null,
            rootMargin: '0px 0px 150px 0px',
            threshold: 0.01,
        });

        observer.observe(sentinel);

        return () => {
            observer.unobserve(sentinel);
            observer.disconnect();
        };
    }, [sourceData, visibleCount, hasMore, onLoadMore, isLoadingMore]);

    const renderedData = React.useMemo((): Entity[] => {
        return sourceData.slice(0, visibleCount);
    }, [sourceData, visibleCount]);

    const resolveRowKey = React.useCallback<(item: Entity, index: number) => string | number>((item, index) => {
        if (item && typeof item === 'object') {
            const record = item as Record<PropertyKey, unknown>;
            const candidate = record.id;

            if (typeof candidate === 'string' || typeof candidate === 'number') {
                return candidate;
            }
        }
        return index;
    }, []);

    const gridTemplateColumns = React.useMemo(() => {
        return [...(columns?.map(column => normalizeGridWidth(column.width)) || [])].join(' ');
    }, [columns]);

    const tableMinWidth = React.useMemo(() => {
        if (!isDesktop || desktopMinWidth == null) {
            return undefined;
        }

        return desktopMinWidth;
    }, [desktopMinWidth, isDesktop]);

    React.useEffect(() => {
        if (!isDesktop) {
            return;
        }

        measure();
    }, [gridTemplateColumns, isDesktop, measure, renderedData.length, tableMinWidth]);

    React.useEffect(() => () => {
        if (suppressClickTimeoutRef.current !== null) {
            window.clearTimeout(suppressClickTimeoutRef.current);
        }

    }, []);

    const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!showScrollbar || event.pointerType === 'mouse' || event.button !== 0) {
            return;
        }

        if (suppressClickTimeoutRef.current !== null) {
            window.clearTimeout(suppressClickTimeoutRef.current);
            suppressClickTimeoutRef.current = null;
        }

        dragStateRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            lastX: event.clientX,
            isDragging: false,
            suppressClick: false,
        };

        event.currentTarget.setPointerCapture(event.pointerId);
    }, [showScrollbar]);

    const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const dragState = dragStateRef.current;

        if (dragState.pointerId !== event.pointerId) {
            return;
        }

        const totalDeltaX = event.clientX - dragState.startX;
        const deltaX = event.clientX - dragState.lastX;

        if (!dragState.isDragging && Math.abs(totalDeltaX) >= DRAG_SCROLL_THRESHOLD) {
            dragState.isDragging = true;
            dragState.suppressClick = true;
        }

        if (!dragState.isDragging) {
            return;
        }

        dragState.lastX = event.clientX;
        event.preventDefault();
        setScrollLeft(currentScrollLeft => currentScrollLeft - deltaX);
    }, [setScrollLeft]);

    const endPointerDrag = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const dragState = dragStateRef.current;

        if (dragState.pointerId !== event.pointerId) {
            return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        dragState.pointerId = -1;
        dragState.isDragging = false;

        if (dragState.suppressClick) {
            suppressClickTimeoutRef.current = window.setTimeout(() => {
                dragState.suppressClick = false;
                suppressClickTimeoutRef.current = null;
            }, 0);
        }
    }, []);

    const handleViewportWheel = React.useCallback((event: React.WheelEvent<HTMLDivElement>) => {
        if (!isDesktop || !showScrollbar || Math.abs(event.deltaX) <= WHEEL_SCROLL_THRESHOLD) {
            return;
        }

        setScrollLeft(currentScrollLeft => currentScrollLeft + event.deltaX);
    }, [isDesktop, setScrollLeft, showScrollbar]);

    const handleRowClick = React.useCallback((item: Entity) => {
        const dragState = dragStateRef.current;

        if (dragState.suppressClick) {
            dragState.suppressClick = false;
            if (suppressClickTimeoutRef.current !== null) {
                window.clearTimeout(suppressClickTimeoutRef.current);
                suppressClickTimeoutRef.current = null;
            }
            return;
        }

        onRowClick?.(item);
    }, [onRowClick]);

    const tableContentStyle = React.useMemo(() => {
        const style: React.CSSProperties = {};

        if (tableMinWidth != null) {
            style.minWidth = tableMinWidth;
        }

        if (scrollLeft > 0) {
            style.marginLeft = `-${scrollLeft}px`;
        }

        return style;
    }, [scrollLeft, tableMinWidth]);

    return {
        isDesktop,
        sentinelRef,
        viewportRef,
        contentRef,
        scrollbarRef,
        contentWidth,
        showScrollbar,
        scrollbarStyle,
        handleScrollbarScroll,
        handlePointerDown,
        handlePointerMove,
        endPointerDrag,
        handleViewportWheel,
        sortConfig,
        handleSortToggle,
        renderedData,
        resolveRowKey,
        gridTemplateColumns,
        tableContentStyle,
        handleRowClick,
    };
}
